import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, CheckCircle2, FileSpreadsheet } from "lucide-react";
import { StaffShell } from "@/components/shells";
import { Panel, Spinner, Select, Btn, Tag, EmptyState } from "@/components/kit";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/vda";

const KINDS = [
  ["inventory", "Vehicle inventory"],
  ["recalls", "Recall notifications"],
  ["mileage_exceptions", "Mileage exceptions"],
  ["fines", "Fines / PCNs"],
  ["maintenance_recharges", "Maintenance recharges"],
];

export default function OperationalImports() {
  const [kind, setKind] = useState("inventory");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const fileRef = useRef();

  const loadHistory = () => api.opImportList().then((r) => setHistory(r.imports || []));
  useEffect(() => { loadHistory(); }, []);

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true); setPreview(null);
    try {
      const res = await api.opImportPreview(kind, file);
      setPreview(res);
      toast.success(`Parsed ${res.row_count} rows — ${res.matched} matched.`);
      loadHistory();
    } catch (e) { toast.error(e.message || "Could not parse the file."); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const apply = async () => {
    if (!preview?.batch_id) return;
    setBusy(true);
    try {
      const res = await api.opImportApply(preview.batch_id);
      toast.success(`Applied ${res.applied} matched rows.`);
      setPreview(null); loadHistory();
    } catch (e) { toast.error(e.message || "Could not apply."); }
    finally { setBusy(false); }
  };

  return (
    <StaffShell>
      <p className="vda-tick text-[hsl(var(--vda-clay))]">Operational records</p>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-1">Monthly file ingestion</h1>
      <p className="text-sm text-muted-foreground mb-5">Upload a monthly operational CSV. Rows are matched to vehicles by registration; nothing is applied until you confirm.</p>

      <Panel className="p-4 mb-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px]">
            <label className="vda-tick text-[hsl(var(--vda-quiet))] block mb-1">File type</label>
            <Select data-testid="opimport-kind" value={kind} onChange={(e) => { setKind(e.target.value); setPreview(null); }}>
              {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
          </div>
          <label data-testid="opimport-upload" className="inline-flex items-center gap-2 h-10 px-4 rounded-[6px] border border-primary text-primary font-medium cursor-pointer hover:bg-primary/5">
            <Upload className="h-4 w-4" /> Choose CSV
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
          </label>
          {busy && <Spinner />}
        </div>
      </Panel>

      {preview && (
        <Panel className="p-0 overflow-hidden mb-5" data-testid="opimport-preview">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="flex gap-2">
              <Tag tone="green">{preview.matched} matched</Tag>
              <Tag tone={preview.unmatched ? "amber" : "muted"}>{preview.unmatched} unmatched</Tag>
              <span className="text-xs text-muted-foreground self-center">Reg column: {preview.reg_column || "not found"}</span>
            </div>
            <Btn data-testid="opimport-apply" onClick={apply} disabled={busy || !preview.matched}>Apply {preview.matched} matched</Btn>
          </div>
          <div className="overflow-x-auto max-h-[360px]">
            <table className="w-full text-sm">
              <thead><tr className="bg-secondary/60 text-left vda-tick text-muted-foreground">
                <th className="px-3 py-2 font-medium">#</th><th className="px-3 py-2 font-medium">Registration</th>
                <th className="px-3 py-2 font-medium">Match</th><th className="px-3 py-2 font-medium">Action</th></tr></thead>
              <tbody>
                {preview.sample.map((r) => (
                  <tr key={r.row_index} className="border-t border-border" data-testid={`opimport-row-${r.row_index}`}>
                    <td className="px-3 py-2 font-mono text-xs">{r.row_index + 1}</td>
                    <td className="px-3 py-2 vda-reg">{r.parsed?.registration || "—"}</td>
                    <td className="px-3 py-2"><Tag tone={r.match_status === "matched" ? "green" : "amber"}>{r.match_status}</Tag></td>
                    <td className="px-3 py-2 text-muted-foreground text-xs">{r.action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      <h2 className="font-semibold mb-2">Recent imports</h2>
      {history.length === 0 ? <EmptyState title="No imports yet" icon={FileSpreadsheet} /> : (
        <Panel className="overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-secondary/60 text-left vda-tick text-muted-foreground">
              <th className="px-3 py-2 font-medium">Type</th><th className="px-3 py-2 font-medium">File</th>
              <th className="px-3 py-2 font-medium">Rows</th><th className="px-3 py-2 font-medium">Matched</th>
              <th className="px-3 py-2 font-medium">Status</th><th className="px-3 py-2 font-medium">When</th></tr></thead>
            <tbody>
              {history.map((b) => (
                <tr key={b.id} className="border-t border-border" data-testid={`opimport-hist-${b.id}`}>
                  <td className="px-3 py-2">{(KINDS.find((k) => k[0] === b.import_kind) || [null, b.import_kind])[1]}</td>
                  <td className="px-3 py-2 text-muted-foreground">{b.filename}</td>
                  <td className="px-3 py-2 font-mono">{b.row_count}</td>
                  <td className="px-3 py-2 font-mono">{b.matched_count}</td>
                  <td className="px-3 py-2"><Tag tone={b.status === "applied" ? "green" : "muted"}>{b.status}{b.status === "applied" ? <CheckCircle2 className="h-3 w-3 inline ml-1" /> : null}</Tag></td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDateTime(b.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </StaffShell>
  );
}
